import { useCallback, useEffect, useState } from "react";
import { Mail } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { getSupabaseClient } from "../providers/supabase/supabase";
import { getResolvedWorkspace } from "../providers/supabase/workspaceConfig";
import { callNylasApi } from "../providers/supabase/nylasApi";

/**
 * Settings → Emails - connect ONE workspace-wide mailbox via Nylas as a
 * fallback marketing/sequence send transport (decision #96), used only when
 * the sending address above isn't set. Unlike CalendarConnectionCard (per
 * sales rep, from their own Profile), this is one connection for the whole
 * tenant, admin-only to connect or disconnect - the backend enforces that;
 * this card just surfaces whatever error comes back for a non-admin.
 *
 * Deliberately a last resort: a real Gmail/Outlook mailbox isn't built for
 * automated bulk-sending volume the way Postmark is (decision #49) - this
 * exists so a workspace can start sending before Postmark's domain
 * verification is done, accepting that tradeoff consciously, not as a
 * parallel or preferred path once Postmark works.
 */

interface Connection {
  provider: string;
  email: string | null;
}

export const MarketingMailboxCard = () => {
  const workspace = getResolvedWorkspace();
  const servicesUrl = (import.meta.env.VITE_CONTROL_PLANE_URL ?? "").replace(/\/+$/, "");
  const slug = workspace?.slug;

  const [connection, setConnection] = useState<Connection | null | "loading">("loading");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [justConnected, setJustConnected] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data, error: err } = await getSupabaseClient()
        .from("marketing_email_connection")
        .select("provider,email")
        .limit(1)
        .maybeSingle();
      if (err) setError(err.message);
      setConnection((data as Connection | null) ?? null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setConnection(null);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("nylas") === "connected") {
      setJustConnected(true);
      // Strip only nylas=connected - keep ?tab=emails so the page stays on
      // this tab (unlike the profile page's own version of this cleanup,
      // this page's tab selection lives in the query string).
      params.delete("nylas");
      const rest = params.toString();
      window.history.replaceState({}, "", window.location.pathname + (rest ? `?${rest}` : ""));
      void load();
    }
  }, [load]);

  const connect = async (provider: "google" | "microsoft") => {
    setBusy(true);
    setError(null);
    try {
      const result = await callNylasApi<{ url: string }>(
        "/integrations/nylas/marketing/connect-url",
        { params: { provider } },
      );
      if (!result) throw new Error("Not signed in.");
      window.location.href = result.url;
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  };

  const disconnect = async () => {
    setBusy(true);
    setError(null);
    try {
      await callNylasApi("/integrations/nylas/marketing/disconnect", { method: "POST" });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  if (!slug || !servicesUrl) return null;

  return (
    <Card>
      <CardContent className="p-4 flex flex-col gap-2.5">
        <div className="flex items-center gap-2">
          <Mail className="w-4 h-4 text-muted-foreground" />
          <p className="text-sm font-medium">
            Backup: connect a mailbox{" "}
            <span className="text-muted-foreground font-normal">
              - used automatically if the address above isn't set
            </span>
          </p>
        </div>
        <p className="text-xs text-muted-foreground">
          Optional and workspace-wide, not personal - one admin connects it
          once. A real inbox isn't built for high send volume the way the
          address above is, so this is meant to get sends going before that's
          set up, not to replace it.
        </p>
        {error && (
          <p role="alert" className="text-xs text-destructive">
            {error}
          </p>
        )}
        {justConnected && !error && (
          <p className="text-xs text-[#6a8f6b]">Mailbox connected.</p>
        )}
        {connection === "loading" ? (
          <p className="text-xs text-muted-foreground">Loading…</p>
        ) : connection ? (
          <div className="flex items-center justify-between gap-2 text-sm">
            <p className="text-muted-foreground">
              Connected as{" "}
              <span className="text-foreground font-medium">{connection.email}</span> (
              {connection.provider})
            </p>
            <Button variant="outline" size="sm" disabled={busy} onClick={() => void disconnect()}>
              {busy ? "Disconnecting…" : "Disconnect"}
            </Button>
          </div>
        ) : (
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={busy} onClick={() => void connect("google")}>
              {busy ? "Connecting…" : "Connect Google"}
            </Button>
            <Button size="sm" variant="outline" disabled={busy} onClick={() => void connect("microsoft")}>
              {busy ? "Connecting…" : "Connect Microsoft"}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
